import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Card from '../ui/Card';
import ConfirmationModal from '../ui/ConfirmationModal';
import { useSettings } from '../../hooks/useSettings';
import { 
  CreditCard, 
  DollarSign, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  Gift,
  Zap
} from 'lucide-react';

const BuyCreditsModal = ({ isOpen, onClose, currentUser }) => {
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMode, setPaymentMode] = useState('manual');
  const [requesting, setRequesting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [stripeAvailable, setStripeAvailable] = useState(false);
  const [stripeMode, setStripeMode] = useState('test');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingPlan, setPendingPlan] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const { safeSettings } = useSettings();

  // Utility functions for browser data collection (Meta CAPI)
  const getBrowserData = () => {
    // Get Facebook Pixel Browser ID (fbp) from cookie
    const getFbp = () => {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === '_fbp') {
          return decodeURIComponent(value);
        }
      }
      return null;
    };

    // Get Facebook Click ID (fbc) from URL or cookie
    const getFbc = () => {
      // First try URL parameter
      const urlParams = new URLSearchParams(window.location.search);
      const fbclid = urlParams.get('fbclid');
      if (fbclid) {
        return `fb.1.${Date.now()}.${fbclid}`;
      }
      
      // Then try cookie
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === '_fbc') {
          return decodeURIComponent(value);
        }
      }
      return null;
    };

    return {
      user_agent: navigator.userAgent,
      fbp: getFbp(),
      fbc: getFbc(),
      source_url: window.location.href
    };
  };

  // Fetch visible pay-as-you-go plans and payment settings
  const fetchAvailablePlans = async () => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
      
      // Get visible plans for users
      const plansResponse = await fetch(
        `${API_BASE_URL}/api/plans`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!plansResponse.ok) {
        throw new Error('Failed to fetch plans');
      }

      const plansData = await plansResponse.json();
      
      // Filter for pay-as-you-go plans (backend already filters for visible and active)
      const payAsYouGoPlans = plansData.filter(plan => 
        plan.plan_type === 'pay_as_you_go'
      );

      setAvailablePlans(payAsYouGoPlans);

      // Get payment settings (user endpoint)
      const settingsResponse = await fetch(
        `${API_BASE_URL}/api/payment-settings`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        setPaymentMode(settingsData.payment_mode || 'manual');
        setStripeMode(settingsData.stripe_mode || 'test');
        setStripeAvailable(!!settingsData.stripe_available);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load available plans');
    } finally {
      setLoading(false);
    }
  };

  // Show confirmation for manual request
  const handleManualRequestClick = (plan) => {
    setPendingPlan(plan);
    setShowConfirmation(true);
  };

  // Handle confirmation dialog
  const handleConfirmRequest = async () => {
    if (!pendingPlan) return;

    setShowConfirmation(false);
    await handleManualRequest(pendingPlan);
    setPendingPlan(null);
  };

  const handleCancelRequest = () => {
    setShowConfirmation(false);
    setPendingPlan(null);
  };

  // Request plan manually (admin assignment)
  const handleManualRequest = async (plan) => {
    try {
      setRequesting(true);
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');

      const orderData = {
        plan_id: plan.id,
        order_type: 'manual_request',
        admin_notes: `User requested ${plan.name} - ${formatPrice(plan.price, plan.currency)}`
      };

      const response = await fetch(
        `${API_BASE_URL}/api/orders/create`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(orderData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create order request');
      }

      alert('Plan request submitted successfully! Admin will review and assign the plan.');
      onClose();

    } catch (err) {
      console.error('Error requesting plan:', err);
      alert('Error submitting request. Please try again.');
    } finally {
      setRequesting(false);
    }
  };

  // Initialize Razorpay payment (for e-commerce mode)
  const handleRazorpayPayment = async (plan) => {
    try {
      setProcessing(true);
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');

      // Collect browser data for Meta CAPI tracking
      const browserData = getBrowserData();

      const orderData = {
        plan_id: plan.id,
        order_type: 'payment_pending',
        browser_data: browserData
      };

      const response = await fetch(
        `${API_BASE_URL}/api/orders/create`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(orderData)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create payment order');
      }

      const result = await response.json();

      // Check integration type returned from backend
      if (result.integration_type === 'payment_links' && result.payment_link_url) {
        // Payment Links mode: Open in new browser tab
        console.log('Opening Razorpay payment link in new tab...');

        // Open payment link in new browser tab (not popup)
        window.open(result.payment_link_url, '_blank');

        // Reset processing state
        setProcessing(false);

      } else if (result.integration_type === 'checkout' && result.razorpay_order && result.razorpay_order.key_id) {
        // Checkout mode: Initialize Razorpay modal (existing flow)
        const options = {
          key: result.razorpay_order.key_id,
          amount: result.razorpay_order.amount,
          currency: result.razorpay_order.currency,
          name: process.env.REACT_APP_PRODUCT_NAME || 'Data Scraper',
          description: plan.name,
          order_id: result.razorpay_order.id,
          handler: async (response) => {
            // Verify payment
            await verifyPayment(response);
          },
          prefill: {
            name: currentUser.full_name,
            email: currentUser.email
          },
          theme: {
            color: 'var(--color-accent)'
          },
          modal: {
            ondismiss: function() {
              console.log('Payment cancelled by user');
              setProcessing(false);
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      } else {
        // Handle configuration errors
        console.error('Razorpay configuration error:', result);
        throw new Error(result.message || 'Payment gateway not properly configured');
      }

    } catch (err) {
      console.error('Error processing payment:', err);
      alert(err.message || 'Error processing payment. Please try again.');
      setProcessing(false);
    }
  };

  // Initialize Stripe payment (redirect Checkout)
  const handleStripePayment = async (plan) => {
    try {
      setProcessing(true);
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');

      // Collect browser data for Meta CAPI tracking
      const browserData = getBrowserData();

      const response = await fetch(
        `${API_BASE_URL}/api/orders/stripe/create-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            plan_id: plan.id,
            browser_data: browserData
          })
        }
      );

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to create Stripe session');
      }

      const data = await response.json();
      if (data.success && data.session_url) {
        window.location.href = data.session_url; // Redirect to Stripe Checkout
      } else {
        throw new Error('Stripe session URL not returned');
      }
    } catch (err) {
      console.error('Error starting Stripe checkout:', err);
      alert('Error starting payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Verify Razorpay payment
  const verifyPayment = async (paymentResponse) => {
    try {
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
      
      const verificationData = {
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature
      };

      const response = await fetch(
        `${API_BASE_URL}/api/orders/verify-payment`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(verificationData)
        }
      );

      if (response.ok) {
        alert('Payment successful! Your plan has been activated.');
        onClose();
        // Refresh page to show updated subscription
        window.location.reload();
      } else {
        alert('Payment verification failed. Please contact support.');
      }
      
    } catch (err) {
      console.error('Error verifying payment:', err);
      alert('Error verifying payment. Please contact support.');
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchAvailablePlans();
    }
  }, [isOpen]);

  const formatPrice = (price, currency = 'INR') => {
    if (currency === 'INR') {
      return `₹${price.toLocaleString('en-IN')}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const getPlanIcon = () => {
    return <Zap size={20} color="#8b5cf6" />;
  };

  if (!isOpen) return null;

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Buy Credits"
      maxWidth="max-w-2xl"
    >
      <div>
        {loading ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '3px solid var(--color-bg-inset)',
                borderTop: '3px solid var(--color-accent)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 12px'
              }} />
              <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>Loading available plans...</p>
            </div>
          </div>
        ) : error ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'var(--color-danger)'
          }}>
            <AlertCircle size={48} style={{ margin: '0 auto 16px' }} />
            <p>{error}</p>
            <Button onClick={fetchAvailablePlans} style={{ marginTop: '16px' }}>
              Retry
            </Button>
          </div>
        ) : availablePlans.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px'
          }}>
            <CreditCard size={48} color="#d1d5db" style={{ margin: '0 auto 16px' }} />
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--color-text-secondary)',
              margin: '0 0 8px 0'
            }}>
              No Plans Available
            </h3>
            <p style={{
              fontSize: '14px',
              color: 'var(--color-text-muted)',
              margin: 0
            }}>
              There are no credit plans available for purchase at the moment.
            </p>
          </div>
        ) : (
          <div>
            {/* Payment Mode Info */}
            <div style={{
              padding: '12px 16px',
              backgroundColor: paymentMode === 'ecommerce' ? '#f0f9ff' : '#fef7f0',
              borderRadius: '8px',
              border: `1px solid ${paymentMode === 'ecommerce' ? '#bae6fd' : '#fed7aa'}`,
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                color: paymentMode === 'ecommerce' ? '#0369a1' : '#c2410c',
                fontWeight: '500'
              }}>
                {paymentMode === 'ecommerce' ? (
                  <>
                    <CreditCard size={16} />
                    💳 Direct Payment Available {stripeAvailable ? '(Stripe)' : '(Razorpay)'}
                  </>
                ) : (
                  <>
                    <Clock size={16} />
                    👨‍💼 Manual Assignment Mode
                  </>
                )}
              </div>
              <p style={{
                fontSize: '12px',
                color: paymentMode === 'ecommerce' ? '#075985' : '#9a3412',
                margin: '4px 0 0 24px'
              }}>
                {paymentMode === 'ecommerce' ? 
                  (stripeAvailable ? 'You will be redirected to Stripe for secure payment.' : 'Payment is processed securely via Razorpay.') :
                  'Submit a request and admin will assign the plan after verification.'
                }
              </p>
            </div>

            {/* Available Plans */}
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'var(--color-text-primary)',
              margin: '0 0 16px 0'
            }}>
              Available Credit Plans
            </h3>

            <div style={{
              display: 'grid',
              gap: '16px',
              gridTemplateColumns: availablePlans.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))'
            }}>
              {availablePlans.map((plan) => (
                <Card key={plan.id} style={{
                  padding: '20px',
                  border: selectedPlan?.id === plan.id ? '2px solid var(--color-accent)' : '1px solid var(--color-border)',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }} onClick={() => setSelectedPlan(plan)}>
                  
                  {/* Plan Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <div style={{
                      padding: '8px',
                      borderRadius: '8px',
                      backgroundColor: '#f3e8ff'
                    }}>
                      {getPlanIcon()}
                    </div>
                    <div>
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: 'var(--color-text-primary)',
                        margin: '0 0 4px 0'
                      }}>
                        {plan.name}
                      </h4>
                      <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500',
                        backgroundColor: '#f3e8ff',
                        color: '#7c3aed'
                      }}>
                        <DollarSign size={12} />
                        Pay-as-you-go
                      </div>
                    </div>
                  </div>

                  {/* Price */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <span style={{
                      fontSize: '24px',
                      fontWeight: '800',
                      color: 'var(--color-text-primary)'
                    }}>
                      {formatPrice(plan.price, plan.currency || 'INR')}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: 'var(--color-text-muted)'
                    }}>
                      one-time
                    </span>
                  </div>

                  {/* Credits */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    <div style={{
                      padding: '4px',
                      borderRadius: '4px',
                      backgroundColor: '#e0e7ff'
                    }}>
                      <Gift size={14} color="#4f46e5" />
                    </div>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#4f46e5'
                    }}>
                      {(plan.credits_granted || 0).toLocaleString()} credits
                    </span>
                  </div>

                  {/* Description */}
                  {plan.description && (
                    <p style={{
                      fontSize: '13px',
                      color: 'var(--color-text-muted)',
                      margin: '0 0 16px 0',
                      lineHeight: '1.4'
                    }}>
                      {plan.description}
                    </p>
                  )}

                  {/* Action Button */}
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (paymentMode === 'ecommerce') {
                        if (stripeAvailable) {
                          handleStripePayment(plan);
                        } else {
                          handleRazorpayPayment(plan);
                        }
                      } else {
                        handleManualRequestClick(plan);
                      }
                    }}
                    disabled={requesting || processing}
                    style={{
                      width: '100%',
                      backgroundColor: selectedPlan?.id === plan.id ? 'var(--color-accent)' : 'var(--color-bg-inset)',
                      color: selectedPlan?.id === plan.id ? 'white' : 'var(--color-text-secondary)',
                      borderColor: selectedPlan?.id === plan.id ? 'var(--color-accent)' : 'var(--color-border-strong)'
                    }}
                  >
                    {requesting || processing ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '14px',
                          height: '14px',
                          border: '2px solid transparent',
                          borderTop: '2px solid currentColor',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        {paymentMode === 'ecommerce' ? 'Processing...' : 'Submitting...'}
                      </div>
                    ) : (
                      paymentMode === 'ecommerce' ? 'Pay Now' : 'Request Plan'
                    )}
                  </Button>
                </Card>
              ))}
            </div>

            {/* Info Footer */}
            <div style={{
              marginTop: '20px',
              padding: '12px',
              backgroundColor: 'var(--color-bg-subtle)',
              borderRadius: '6px',
              fontSize: '12px',
              color: 'var(--color-text-muted)'
            }}>
              <p style={{ margin: '0 0 4px 0' }}>
                <strong>💡 How it works:</strong>
              </p>
              <ul style={{ margin: 0, paddingLeft: '16px' }}>
                <li>Pay-as-you-go plans give you credits that never expire</li>
                <li>Credits are used for data scraping jobs</li>
                <li>{paymentMode === 'ecommerce' ? (stripeAvailable ? 'You will be redirected to Stripe for payment' : 'Payment is processed securely') : 'Requests are reviewed by admin before assignment'}</li>
                <li>Credits are added to your account immediately after {paymentMode === 'ecommerce' ? 'payment' : 'approval'}</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </Modal>

    {/* Confirmation Modal for Manual Requests */}
    <ConfirmationModal
      isOpen={showConfirmation}
      onConfirm={handleConfirmRequest}
      onCancel={handleCancelRequest}
      title="Confirm Plan Request"
      message={`Are you sure you want to request this plan? Clicking yes will send a request to the ${safeSettings?.product_name || 'Apollo Exporter'} team.`}
      confirmText="Yes, Request Plan"
      cancelText="Cancel"
      type="warning"
      loading={requesting}
    />
  </>
  );
};

export default BuyCreditsModal;